import React, { useState, useEffect } from 'react';
import SchoolDashboard from './SchoolDashboard';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import DashboardService from '../services/dashboard.service';
import { useTranslation } from '../hooks/useTranslation';
import { formatCurrencySync, getCurrencySymbolSync } from '../utils/currencyUtils';
import LoyaltyDashboardSection from './loyalty/LoyaltyDashboardSection';
import MenuDashboardSection from './menus/MenuDashboardSection';

const Dashboard = () => {
  const { user, permissions, roles, hasPermission } = useAuth();
  const isLoyaltyProfile = user?.tenant?.profile === 'loyalty';
  const isMenuProfile = user?.tenant?.profile === 'menu';
  const loyaltyEnabled = (isLoyaltyProfile || !!user?.tenant?.settings?.loyalty?.enabled) && hasPermission('loyalty.view');
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.tenant?.profile !== 'school' && !isMenuProfile && !(isLoyaltyProfile && loyaltyEnabled)) {
      loadDashboardData();
    }
  }, [user]);

  if (user?.tenant?.profile === 'school') {
    return <SchoolDashboard />;
  }

  // Menu-profile tenants (restaurants on the digital menu): home is the menu dashboard.
  if (isMenuProfile) {
    return <MenuDashboardSection />;
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await DashboardService.getStats();
      if (response.data.success) {
        setDashboardData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load dashboard data';
      setError(errorMessage);
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (changeType) => {
    switch (changeType) {
      case 'increase':
        return <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'decrease':
        return <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      default:
        return <TrendingFlatIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    }
  };

  const getTrendColor = (changeType) => {
    switch (changeType) {
      case 'increase':
        return 'success.main';
      case 'decrease':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const formatChangeText = (data, type) => {
    if (!data || !data.change) return t('dashboard.nodata');
    
    const { change } = data;
    const absValue = Math.abs(change.value);
    
    switch (type) {
      case 'clients':
        return change.type === 'increase' 
          ? `+${absValue} `+t('dashboard.newCustomers')
          : change.type === 'decrease'
          ? `-${absValue} fewer clients this month`
          : t('dashboard.msgchangeclients');   
      
      case 'services':
        return change.type === 'increase' 
          ? `+${absValue} more services completed this month`
          : change.type === 'decrease'
          ? `-${absValue} fewer services completed this month`
          :  t ('dashboard.msgservicesame');
      
      case 'income':
        const symbol = getCurrencySymbolSync();
        return change.type === 'increase' 
          ? `+${symbol}${absValue.toLocaleString()} more revenue this month`
          : change.type === 'decrease'
          ? `-${symbol}${absValue.toLocaleString()} less revenue this month`
          : t ('dashboard.msgincomesame');
      
      
      default:
        return t('dashboard.nodata');
    }
  };

  // Loyalty-profile tenants: the points program is the business, so the home
  // dashboard is the loyalty dashboard.
  if (isLoyaltyProfile && loyaltyEnabled) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>{t('loyalty.dashboardTitle')}</Typography>
        <LoyaltyDashboardSection />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const stats = dashboardData ? [
    {
      title: t('dashboard.totalClients'),
      value: dashboardData.totalClients.current.toLocaleString(),
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      change: dashboardData.totalClients.change,
      changeText: formatChangeText(dashboardData.totalClients, 'clients')
    },
    {
      title: t('dashboard.servicesCompleted'),
      value: dashboardData.activeServices.current.toLocaleString(),
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      change: dashboardData.activeServices.change,
      changeText: formatChangeText(dashboardData.activeServices, 'services')
    },
    {
      title: t('dashboard.totalIncome'),
      value: formatCurrencySync(dashboardData.totalIncome.current),
      change: dashboardData.totalIncome.change,
      changeText: formatChangeText(dashboardData.totalIncome, 'income')
    }
  ] : [];

  const recentActivities = dashboardData?.recentActivities || [];

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {t ('dashboard.Welcome')}, {user?.name || 'User'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
        {t ('dashboard.msgwelcome')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Stats Cards */}
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                      {stat.change && getTrendIcon(stat.change.type)}
                      <Typography variant="body2" color={stat.change ? getTrendColor(stat.change.type) : 'text.secondary'}>
                        {stat.changeText || 'No data available'}
                      </Typography>
                    </Box>
                  </Box>
                  {stat.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* User Info & Permissions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t ('dashboard.yourprofile')}
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {user?.name || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email || 'user@example.com'}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t ('dashboard.yourroles')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {roles?.map(role => (
                  <Chip
                    key={typeof role === 'string' ? role : role.name}
                    label={typeof role === 'string' ? role : role.name}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t ('dashboard.keyp')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {permissions?.slice(0, 8).map(permission => (
                  <Chip
                    key={typeof permission === 'string' ? permission : permission.name}
                    label={typeof permission === 'string' ? permission.replace('.', ' ') : permission.name.replace('.', ' ')}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                ))}
                {permissions?.length > 8 && (
                  <Chip
                    label={`+${permissions.length - 8} more`}
                    color="default"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  {t('dashboard.recentActivities')}
                </Typography>
              </Box>
              <List>
                {recentActivities.map((activity, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main'
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.action}
                      secondary={activity.time}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loyaltyEnabled && <LoyaltyDashboardSection />}
    </Box>
  );
};

export default Dashboard;
